/**
 * Application state.
 *
 * Two kinds of state live here and they behave differently:
 *   - server truth (session, snapshot) — replaced wholesale when it arrives;
 *   - view state (selection, filters, search) — owned by the user, and must
 *     survive a snapshot arriving. Selecting a VM and having the next poll
 *     deselect it would make the map unusable, so selection is stored by id
 *     and re-resolved against each new snapshot rather than holding a node.
 */

import { create } from 'zustand';
import type {
  ApiToken,
  CreateApiTokenRequest,
  CreateApiTokenResponse,
  DirectoryResponse,
  SessionInfo,
  Settings,
  Snapshot,
  UserListResponse,
} from '@proxmon/shared';
import { api, ApiError } from '../api/client.ts';
import { DEFAULT_ZOOM } from '../panels/backups.ts';
import { openSnapshotStream } from '../api/stream.ts';

export interface Filters {
  hideStopped: boolean;
  hideTemplates: boolean;
  edgeOnly: boolean;
  /** draw bridges as nodes; when off, the segment name rides on the link */
  showBridges: boolean;
}

export interface StoreState {
  session: SessionInfo | null;
  snapshot: Snapshot | null;
  settings: Settings | null;
  /** null until the settings dialog asks for them */
  tokens: ApiToken[] | null;
  connected: boolean;
  booting: boolean;
  /** transient, user-facing message shown as a toast */
  notice: { kind: 'error' | 'info'; text: string } | null;

  selectedId: string | null;
  hoveredId: string | null;
  search: string;
  filters: Filters;
  /** set by the map when the user asks to recentre; consumed by the canvas */
  fitRequest: number;
  /** the backup timeline at the bottom of the window is unfolded */
  backupsOpen: boolean;
  /**
   * The legend box over the map is open. Closed to begin with: it explains a
   * map you only need explained once, and the map is what the window is for.
   */
  legendOpen: boolean;
  /** how wide an hour is on the backup timeline, in pixels; a ZOOM_STOPS value */
  timelineZoom: number;
  /**
   * The sign-in form is showing instead of first-run setup. Only reachable
   * from the setup screen, and only with a directory configured: the first
   * domain administrator has an account already and should not have to invent
   * a local one to get in.
   */
  showLogin: boolean;

  bootstrap(): Promise<void>;
  login(username: string, password: string): Promise<void>;
  setup(username: string, password: string): Promise<void>;
  logout(): Promise<void>;
  changePassword(currentPassword: string, newPassword: string): Promise<void>;
  /*
   * Access-list calls. They return their data instead of parking it in the
   * store: only the Users dialog ever needs it, and stale rows sitting in
   * global state would be worse than refetching when it opens.
   */
  listUsers(): Promise<UserListResponse>;
  listDirectory(): Promise<DirectoryResponse>;
  addUser(username: string, role: 'admin' | 'viewer', displayName?: string, email?: string): Promise<void>;
  setUserRole(username: string, role: 'admin' | 'viewer'): Promise<void>;
  setUserEnabled(username: string, enabled: boolean): Promise<void>;
  removeUser(username: string): Promise<void>;
  testDirectory(): Promise<{ ok: boolean; server?: string; baseDn?: string; sample?: number; error?: string }>;
  updateSettings(patch: Partial<Settings>): Promise<void>;
  loadTokens(): Promise<void>;
  createToken(input: CreateApiTokenRequest): Promise<CreateApiTokenResponse>;
  revokeToken(id: number): Promise<void>;
  setIpOverride(mac: string, address: string | null, note?: string | null): Promise<void>;
  /** `text` is what the operator typed ("1G", "2.5G"); null or empty clears it. */
  setBridgeCapacity(node: string, iface: string, text: string | null): Promise<void>;
  refresh(): Promise<void>;

  select(id: string | null): void;
  hover(id: string | null): void;
  setSearch(value: string): void;
  setFilter<K extends keyof Filters>(key: K, value: Filters[K]): void;
  requestFit(): void;
  toggleBackups(): void;
  toggleLegend(): void;
  setTimelineZoom(pxPerHour: number): void;
  setShowLogin(value: boolean): void;
  notify(kind: 'error' | 'info', text: string): void;
  dismissNotice(): void;
}

let stopStream: (() => void) | null = null;

function describe(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong.';
}

export const useStore = create<StoreState>((set, get) => ({
  session: null,
  snapshot: null,
  settings: null,
  tokens: null,
  connected: false,
  booting: true,
  notice: null,

  selectedId: null,
  hoveredId: null,
  search: '',
  filters: { hideStopped: false, hideTemplates: true, edgeOnly: false, showBridges: true },
  fitRequest: 0,
  backupsOpen: true,
  legendOpen: false,
  timelineZoom: DEFAULT_ZOOM,
  showLogin: false,

  async bootstrap() {
    try {
      const session = await api.get<SessionInfo>('/api/session');
      set({ session, booting: false });
      if (session.authenticated) {
        await get().refresh();
        startStream(set, get);
        if (session.authenticated) void loadSettings(set);
      } else {
        stopStream?.();
        stopStream = null;
        set({ snapshot: null, connected: false });
      }
    } catch (err) {
      set({ booting: false, notice: { kind: 'error', text: describe(err) } });
    }
  },

  async login(username, password) {
    const session = await api.post<SessionInfo>('/api/login', { username, password });
    set({ session, showLogin: false });
    await get().bootstrap();
  },

  async setup(username, password) {
    const session = await api.post<SessionInfo>('/api/setup', { username, password });
    set({ session });
    await get().bootstrap();
  },

  async logout() {
    stopStream?.();
    stopStream = null;
    const session = await api.post<SessionInfo>('/api/logout');
    set({ session, snapshot: null, settings: null, tokens: null, connected: false, selectedId: null });
    // Re-bootstrap rather than assuming what the server will say next: it also
    // reports whether first-run setup has reopened, which decides the screen.
    await get().bootstrap();
  },

  async changePassword(currentPassword, newPassword) {
    await api.post('/api/password', { currentPassword, newPassword });
    get().notify('info', 'Password changed. Other sessions were signed out.');
  },

  async listUsers() {
    return api.get<UserListResponse>('/api/users');
  },

  async listDirectory() {
    return api.get<DirectoryResponse>('/api/users/directory');
  },

  async addUser(username, role, displayName, email) {
    await api.post('/api/users', { username, role, displayName, email });
  },

  async setUserRole(username, role) {
    await api.put(`/api/users/${encodeURIComponent(username)}/role`, { role });
    // Demoting yourself changes what the UI may show, so re-read the session.
    set({ session: await api.get<SessionInfo>('/api/session') });
  },

  async setUserEnabled(username, enabled) {
    await api.put(`/api/users/${encodeURIComponent(username)}/enabled`, { enabled });
  },

  async removeUser(username) {
    await api.del(`/api/users/${encodeURIComponent(username)}`);
  },

  async testDirectory() {
    return api.post<{ ok: boolean; server?: string; baseDn?: string; sample?: number; error?: string }>(
      '/api/users/directory/test',
    );
  },

  async updateSettings(patch) {
    const settings = await api.patch<Settings>('/api/settings', patch);
    set({ settings });
    const session = await api.get<SessionInfo>('/api/session');
    set({ session });
  },

  async loadTokens() {
    set({ tokens: await api.get<ApiToken[]>('/api/tokens') });
  },

  /**
   * The response carries the secret, and this is the only moment it exists
   * outside the caller's own config. It is handed straight back to the caller
   * for display and deliberately never stored in the store.
   */
  async createToken(input) {
    const created = await api.post<CreateApiTokenResponse>('/api/tokens', input);
    await get().loadTokens();
    return created;
  },

  async revokeToken(id) {
    await api.del(`/api/tokens/${id}`);
    await get().loadTokens();
  },

  async setIpOverride(mac, address, note) {
    await api.put(`/api/ip-overrides/${encodeURIComponent(mac)}`, { address, note: note ?? null });
    await get().refresh();
  },

  async setBridgeCapacity(node, iface, text) {
    const path = `/api/bridge-capacity/${encodeURIComponent(node)}/${encodeURIComponent(iface)}`;
    await api.put(path, { text: text?.trim() ? text.trim() : null });
    await get().refresh();
  },

  async refresh() {
    try {
      const snapshot = await api.get<Snapshot>('/api/snapshot');
      set({ snapshot });
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        // First poll has not finished yet; the stream will deliver it.
        return;
      }
      if (err instanceof ApiError && err.isAuthError) {
        set({ snapshot: null });
        return;
      }
      set({ notice: { kind: 'error', text: describe(err) } });
    }
  },

  select(id) {
    set({ selectedId: id });
  },
  hover(id) {
    set({ hoveredId: id });
  },
  setSearch(value) {
    set({ search: value });
  },
  setFilter(key, value) {
    set((state) => ({ filters: { ...state.filters, [key]: value } }));
  },
  requestFit() {
    set((state) => ({ fitRequest: state.fitRequest + 1 }));
  },
  toggleBackups() {
    set((state) => ({ backupsOpen: !state.backupsOpen }));
  },
  toggleLegend() {
    set((state) => ({ legendOpen: !state.legendOpen }));
  },
  setTimelineZoom(pxPerHour) {
    set({ timelineZoom: pxPerHour });
  },
  setShowLogin(value) {
    set({ showLogin: value });
  },
  notify(kind, text) {
    set({ notice: { kind, text } });
  },
  dismissNotice() {
    set({ notice: null });
  },
}));

async function loadSettings(set: (partial: Partial<StoreState>) => void): Promise<void> {
  try {
    set({ settings: await api.get<Settings>('/api/settings') });
  } catch {
    // Non-fatal: the settings dialog will retry when opened.
  }
}

function startStream(
  set: (partial: Partial<StoreState>) => void,
  get: () => StoreState,
): void {
  stopStream?.();
  stopStream = openSnapshotStream({
    onSnapshot: (snapshot) => set({ snapshot }),
    onStateChange: (connected) => set({ connected }),
    onLost: () => {
      set({ connected: false });
      // Most likely the session expired, or the account was suspended.
      // Re-checking it is what tells us which, and puts up the right screen.
      void get().bootstrap();
    },
  });
}
